/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */


import React, { useEffect, useRef } from 'react';
import { useSocketStore } from '@/stores/use-external-socket-store.ts';
import { useNavigate } from 'react-router';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { TPRESENCE_ADDRESS } from '@/config.ts';
import { toast } from 'sonner';
import { SessionUser } from '@/types/user.ts';
import { SocketContext } from '@/context/socket-context/external-socket-context.tsx';

/**
 * @beta
 * @param children
 * @constructor
 */
export const ExternalSocketProviderWithReconnect: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { socket, setSocket } = useSocketStore();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const reconnectAttempts = useRef(0);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastPing = useRef<number>(Date.now());

  const connectWebSocket = () => {
    if (websocketRef.current) return; // avoid duplicates
    if (!user?.id) return;

    const ws = new WebSocket(TPRESENCE_ADDRESS);
    websocketRef.current = ws;

    ws.onopen = () => {
      console.info('[ESPWR] Connected');
      reconnectAttempts.current = 0;
      setSocket(ws);

      ws.send(
        JSON.stringify({
          type: 'identify',
          userId: user.id,
        })
      );
    };

    ws.onmessage = (event: MessageEvent) => {
      lastPing.current = Date.now();
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          // @experimental
          case 'old-move-user-to-channel':
            // Only the moved user processes this event; admin/initiator is excluded
            if (data.userId !== user?.id) return;

            // Navigate to the new channel — ChannelElement will auto-join the new call
            navigate(`/servers/${data.serverId}/channels/${data.destChannelId}`, {
              state: { fromPreview: true },
            });
            break;

          case 'move-user-to-channel': {
            // Событие предназначено только для перемещённого пользователя.
            if (String(data.userId) !== String(user?.id)) return;

            if (!data.serverId || !data.destChannelId) {
              // console.error('[ESPWR] Invalid move-user-to-channel payload', data);
              return;
            }
 
            window.dispatchEvent(
              new CustomEvent('calls:move-user-to-channel', {
                detail: {
                  serverId: String(data.serverId),
                  destChannelId: String(data.destChannelId),
                },
              })
            );

            break;
          }

          case 'remove-user-from-channel':
            if (data.userId === user?.id) {
              navigate(`/servers/${data.serverId}`, { replace: true });
              window.location.reload();
              toast.info('Вас исключили из канала');
            }
            break;

          case 'channel-edit': {
            window.dispatchEvent(
              new CustomEvent('calls:channel-edit', {
                detail: { channelId: data.payload?.id, channelType: data.payload?.type },
              })
            );
            break;
          }

          case 'user:update':
            if (data.id === user?.id) {
              // Update only those from user-settings
              setUser((prev: SessionUser) =>
                prev
                  ? {
                      ...prev,
                      name: (data as SessionUser).name,
                      image: (data as SessionUser).image,
                      phone: (data as SessionUser).phone,
                      email: (data as SessionUser).email,
                    }
                  : prev
              );
            }
            break;

          case 'call-invite':
            if (data.fromUserId === user?.id) {
              // showIncomingCall({
              //   roomId: data.roomId,
              //   channelId: data.channelId,
              //   caller: data.from,
              // });
            }
            break;

          case 'reorder-channels':
            // TODO: dynamically reorder channel/fetch new on clients
            // data.serverId
            // data.channels
            break;
        }
      } catch (err) {
        console.error('[ESPWR] Message parse error', err);
      }
    };

    ws.onclose = () => {
      console.warn('[ESPWR] Disconnected');
      setSocket(null);
      websocketRef.current = null;
      scheduleReconnect();
    };

    ws.onerror = (err) => {
      console.error('[ESPWR] Error', err);
      ws.close();
    };
  };

  const scheduleReconnect = () => {
    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    reconnectAttempts.current++;

    const delay = Math.min(30_000, 1000 * 2 ** reconnectAttempts.current) + Math.random() * 1000;
    console.info(`[ESPWR] Reconnecting in ${delay / 1000}s...`);

    reconnectTimeout.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  };

  // Detect network / wake-up events
  useEffect(() => {
    const handleNetworkChange = () => {
      if (navigator.onLine) {
        console.info('[ESPWR] Network online, reconnecting...');
        reconnectAttempts.current = 0;
        if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN)
          connectWebSocket();
      } else {
        console.warn('[ESPWR] Network offline');
        websocketRef.current?.close();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // app reopened or resumed from sleep
        const now = Date.now();
        if (now - lastPing.current > 10_000) {
          console.info('[ESPWR] Resumed from sleep, reconnecting...');
          websocketRef.current?.close();
        }
      }
    };

    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    connectWebSocket();

    return () => {
      window.removeEventListener('online', handleNetworkChange);
      window.removeEventListener('offline', handleNetworkChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      websocketRef.current?.close();
      websocketRef.current = null;
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};
